/**
 * Global Search — across tenants, subscriptions, audit events, notifications
 */
import React, { useState } from "react";
import { search as searchApi } from "../api.js";
import { useAsync, useDebounce } from "../hooks.js";
import type { OpsSearchResult, OpsSearchResultType } from "@bizzness/shared-types";
import { navigate } from "../router.js";

const TYPE_ICON: Record<OpsSearchResultType, string> = {
  tenant: "🏢",
  subscription: "💳",
  audit_event: "📋",
  notification: "🔔",
};

export function SearchView() {
  const [query, setQuery] = useState("");
  const dq = useDebounce(query, 350);

  const { data, loading, error } = useAsync<any>(
    () => dq.length >= 2 ? searchApi.query(dq) : Promise.resolve(null),
    [dq]
  );

  const results: OpsSearchResult[] = data?.results ?? [];

  return (
    <div className="ops-page">
      <div className="ops-page-header">
        <div>
          <h1 className="ops-page-title">Search</h1>
          <p className="ops-page-subtitle">Search across tenants, audit events, and notifications</p>
        </div>
      </div>

      <div style={{ maxWidth: 600, marginBottom: "1.5rem" }}>
        <input
          className="ops-input"
          style={{ fontSize: "1rem", padding: "0.625rem 0.875rem" }}
          placeholder="Search by slug, email, name, tenant ID…"
          value={query}
          onChange={e => setQuery(e.target.value)}
          autoFocus
        />
      </div>

      {error && <div className="ops-error">{error}</div>}
      {loading && query.length >= 2 && <div className="ops-loading">Searching…</div>}

      {!loading && query.length >= 2 && results.length === 0 && data !== null && (
        <div className="ops-empty">No results for "{query}"</div>
      )}

      {query.length < 2 && !loading && (
        <div className="ops-empty" style={{ paddingTop: "3rem" }}>
          Type at least 2 characters to search.
        </div>
      )}

      {results.length > 0 && (
        <div style={{ display: "flex", flexDirection: "column", gap: "0.5rem" }}>
          <div style={{ fontSize: "0.75rem", color: "var(--ops-muted)", marginBottom: "0.25rem" }}>
            {data.total} result{data.total !== 1 ? "s" : ""}
          </div>
          {results.map(r => (
            <button
              key={r.id}
              className="ops-card"
              style={{
                cursor: "pointer", textAlign: "left", border: "1px solid var(--ops-border)",
                background: "var(--ops-surface)", transition: "border-color 0.15s",
              }}
              onClick={() => {
                if (r.href.startsWith("/tenants")) navigate("tenants");
                else if (r.href.startsWith("/audit")) navigate("audit-log");
                else if (r.href.startsWith("/notif")) navigate("notifications");
              }}
              onMouseEnter={e => (e.currentTarget.style.borderColor = "var(--ops-accent)")}
              onMouseLeave={e => (e.currentTarget.style.borderColor = "var(--ops-border)")}
            >
              <div className="ops-row">
                <span style={{ fontSize: "1.125rem" }}>{TYPE_ICON[r.type]}</span>
                <div style={{ flex: 1 }}>
                  <div style={{ fontWeight: 600, fontSize: "0.875rem" }}>{r.title}</div>
                  {r.subtitle && (
                    <div style={{ fontSize: "0.75rem", color: "var(--ops-muted)", marginTop: "0.125rem" }}>
                      {r.subtitle}
                    </div>
                  )}
                </div>
                <span className={`ops-badge ops-badge-neutral`} style={{ flexShrink: 0 }}>
                  {r.type.replace("_", " ")}
                </span>
                {r.relevantAt && (
                  <span style={{ fontSize: "0.7rem", color: "var(--ops-muted)", flexShrink: 0 }}>
                    {r.relevantAt.slice(0, 10)}
                  </span>
                )}
              </div>
            </button>
          ))}
        </div>
      )}
    </div>
  );
}
